/**
 * Générateur — Automatisme : factoriser un nombre entier positif :
 * 60 = 2² × 3 × 5.
 * Attendu ID : 1221 | Niveau : 3ème | Domaine : Multiples et diviseurs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 781 :
 * « Factoriser un nombre entier positif : 60 = 2² × 3 × 5. »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Le sens DIRECT seulement : un nombre → son produit de facteurs premiers,
 * exposants compris (l'écriture de 1217 appliquée à des facteurs
 * différents). Extension de gen_154 (4e, objectif) en trois cartes
 * d'automatisme ; les cartes « Retrouve le nombre » et « Combien de fois »
 * de 154 ne sont pas reprises (sens inverse ; la lecture d'un exposant vit
 * au Vrai/Faux). Frontières : irréductible (l. 731, gen_206), PGCD (205),
 * nombres premiers (153).
 * PLAGE : facteurs 2, 3, 5, 7 seulement (les tables), au moins deux facteurs
 * premiers distincts et trois facteurs comptés avec multiplicité (deux =
 * 1041, une puissance pure = 1103/1217) ; 12 ≤ n ≤ 100 deux lignes sur
 * trois, n ≤ 200 sinon ; un exposant ≥ 2 une fois sur deux (30 = 2 × 3 × 5
 * discrimine sans exposant). Banque MathsMentales (S. Cogez, Apache 2.0,
 * relevé 2026-09-12) : 5ND1 options 0-1 (≤ 100) retenues ; l'option 2
 * « 101 à 1 000 (3e) » est le niveau de l'objectif ancien (division posée),
 * pas d'un automatisme en dix secondes (Eduscol 2020 : « inférieurs à 30 »
 * en 4e, « décomposer 60 » en test de fin de 4e). À ARBITRER : 360 et
 * 1 000 admis ? le mot « premier » dans les consignes (absent du programme
 * 2025, gardé ici : c'est le nom que l'élève connaît).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     « 60 = 2^⬚ × 3 × ⬚ » (un trou exposant, un trou facteur ; sans
 *     exposant : deux trous facteurs « 30 = ⬚ × 3 × ⬚ »), facteurs
 *     croissants, une ligne ≤ 200 par série, quatre nombres distincts.
 *   Carte 2 « La bonne décomposition » (decomposition_qcm_unique) : QUATRE
 *     choix [2² × 3 × 5 ; 4 × 3 × 5 ; 2 × 3 × 5 ; 2³ × 3 × 5] ; le
 *     distracteur produit_de_tables a la MÊME VALEUR (faux par la forme :
 *     4 n'est pas premier), la seule exception assumée à la règle des
 *     valeurs distinctes ; les autres ont une valeur différente.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : quatre lignes vérifiées EN
 *     VALEUR ou en FORME : « 60 = 2² × 3 × 5 » V ; « 60 = 2 × 3 × 5 » F ;
 *     « 2³ × 5 = 30 » F (base_fois_exposant) ; « 2² × 3 × 5 = 2 × 2 × 3 × 5 »
 *     V ; « La décomposition de 60 en produit de facteurs premiers est
 *     4 × 15 » F (forme).
 *
 * INVARIANTS : chaque nombre est CONSTRUIT depuis ses facteurs, puis la
 * valeur de chaque écriture est re-calculée (throw si ≠) ; jamais 2² et
 * 2 × 2 en distracteurs l'un de l'autre au QCM (même valeur, même forme
 * développée : seule la forme avec exposant est la clé, l'écriture
 * développée est une ligne VRAIE du Vrai/Faux).
 * SACRIFIÉ (fiche d'aide « 60 = 6 × 10, 6 = 2 × 3, 10 = 2 × 5 ») : 60,
 * l'exemple du programme, sur tout ce qui s'affiche (auto-testé par
 * gen1221.self.test.js ; alternative 90 À ARBITRER).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   produit_de_tables  : 60 = 4 × 3 × 5 (un facteur non premier, même
 *                        valeur : la réponse de 1041, vraie et pas finie),
 *                        PRINCIPALE ;
 *   exposant_oublie    : 2 × 3 × 5 (= 30) ;
 *   exposant_faux      : 2³ × 3 × 5 (= 120) ;
 *   facteur_manquant   : 2² × 3 (= 12) ;
 *   base_fois_exposant : 2³ lu 2 × 3 (Vrai/Faux : « 2³ × 5 = 30 » F ; jamais
 *                        sur 2², dont la valeur coïncide).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1221;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'La bonne décomposition';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

const PREMIERS = [2, 3, 5, 7];
/** L'exemple du programme et de la fiche. */
export const SACRIFIE = 60;

/** La valeur d'une liste de facteurs [{ p, exp }]. */
const valeurDe = (facteurs) => facteurs.reduce((acc, f) => acc * f.p ** f.exp, 1);

/**
 * Un nombre { n, facteurs } construit depuis ses facteurs : 2 ou 3 premiers
 * distincts parmi 2, 3, 5, 7, exposants 1..3, au moins trois facteurs avec
 * multiplicité, 12 ≤ n ≤ `max`, jamais 60 ; `avecExposant` impose (ou
 * interdit) un exposant ≥ 2.
 */
const tirer = ({ max = 100, avecExposant = null, exclure = [] } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const nb = randInt(2, 3);
        const primes = shuffle([...PREMIERS]).slice(0, nb).sort((u, v) => u - v);
        const facteurs = primes.map((p) => ({ p, exp: randInt(1, 3) }));
        const total = facteurs.reduce((s, f) => s + f.exp, 0);
        const n = valeurDe(facteurs);
        const aExposant = facteurs.some((f) => f.exp >= 2);
        if (total < 3 || n < 12 || n > max || n === SACRIFIE || exclure.includes(n)) continue;
        if (avecExposant === true && !aExposant) continue;
        if (avecExposant === false && aExposant) continue;
        return { n, facteurs };
    }
    return { n: 90, facteurs: [{ p: 2, exp: 1 }, { p: 3, exp: 2 }, { p: 5, exp: 1 }] };
};

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const facteurTex = ({ p, exp }) => (exp === 1 ? String(p) : `${p}^{${exp}}`);
const decompoTex = (facteurs) => facteurs.map(facteurTex).join(' \\times ');
const developpeTex = (facteurs) => facteurs.flatMap((f) => Array(f.exp).fill(String(f.p))).join(' \\times ');
/** Assert : l'écriture vaut bien n. */
const verifier = (n, facteurs) => { if (valeurDe(facteurs) !== n) throw new Error(`gen_1221 : ${decompoTex(facteurs)} ≠ ${n}`); };

const EXPLICATIONS = {
    produit_de_tables: '4 × 3 × 5 vaut bien 60, mais 4 n\'est pas premier : 4 = 2 × 2, donc 60 = 2² × 3 × 5. On continue jusqu\'à des facteurs qui ne se cassent plus.',
    exposant_oublie: '2 × 3 × 5 = 30, pas 60 : il manque un 2. Le petit 2 dit que le facteur 2 apparaît deux fois.',
    exposant_faux: 'Je vérifie en multipliant : 2³ × 3 × 5 = 8 × 15 = 120, pas 60.',
    facteur_manquant: 'Je vérifie en multipliant : 2² × 3 = 12, il manque le facteur 5.',
    base_fois_exposant: '2³, c\'est 2 × 2 × 2 = 8, pas 2 × 3.',
    regle: 'Je casse le nombre avec les tables (60 = 6 × 10), puis chaque morceau (6 = 2 × 3, 10 = 2 × 5), jusqu\'à des facteurs premiers ; les facteurs répétés s\'écrivent avec un exposant.',
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète</b> chaque décomposition en produit de facteurs premiers.</p>';

/** Une ligne : un trou sur un exposant ≥ 2 (s'il y en a) et un trou sur un autre facteur ; sinon deux trous facteurs. */
const ligneFlash = ({ n, facteurs }) => {
    verifier(n, facteurs);
    const avecExp = facteurs.map((f, i) => (f.exp >= 2 ? i : -1)).filter((i) => i >= 0);
    const iExp = avecExp.length ? randomChoice(avecExp) : -1;
    const iFact = randomChoice(facteurs.map((_, i) => i).filter((i) => i !== iExp));
    const iFact2 = iExp === -1 ? randomChoice(facteurs.map((_, i) => i).filter((i) => i !== iFact)) : -1;
    const morceaux = facteurs.map((f, i) => {
        const base = i === iFact || i === iFact2 ? `{{${f.p}}}` : String(f.p);
        if (f.exp === 1) return base;
        return i === iExp ? `${base}^{ {{${f.exp}}} }` : `${base}^{${f.exp}}`;
    });
    return `$${n} = ${morceaux.join(' \\times ')}$`;
};

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        const grand = randInt(0, 3);
        for (let i = 0; i < 4; i++) {
            items.push(tirer({ max: i === grand ? 200 : 100, avecExposant: i === 0 ? true : i === 1 ? false : null, exclure: items.map((it) => it.n) }));
        }
        if (new Set(items.map((it) => it.n)).size < 4) continue;
        const payload = makeTexteTrou(items.map(ligneFlash).join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, exposant_oublie: EXPLICATIONS.exposant_oublie } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [
        { n: 90, facteurs: [{ p: 2, exp: 1 }, { p: 3, exp: 2 }, { p: 5, exp: 1 }] },
        { n: 30, facteurs: [{ p: 2, exp: 1 }, { p: 3, exp: 1 }, { p: 5, exp: 1 }] },
        { n: 28, facteurs: [{ p: 2, exp: 2 }, { p: 7, exp: 1 }] },
        { n: 150, facteurs: [{ p: 2, exp: 1 }, { p: 3, exp: 1 }, { p: 5, exp: 2 }] },
    ];
    const payload = makeTexteTrou(items.map(ligneFlash).join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : La bonne décomposition (QCM) ───────────────────────────────

/** Les candidats, chacun avec sa liste de facteurs (bases non forcément premières) ; jamais la clé. */
const candidats = ({ facteurs }) => {
    const c = [];
    // produit_de_tables : deux facteurs p regroupés en p × p (2² → 4 ; 2³ → 2 × 4), sinon les deux premiers facteurs fusionnés (2 × 3 → 6)
    const iCarre = facteurs.findIndex((f) => f.exp >= 2);
    if (iCarre >= 0) {
        const f = facteurs[iCarre];
        const reste = f.exp - 2 >= 1 ? [{ p: f.p, exp: f.exp - 2 }] : [];
        c.push({ code: 'produit_de_tables', facteurs: [...facteurs.slice(0, iCarre), ...reste, { p: f.p * f.p, exp: 1 }, ...facteurs.slice(iCarre + 1)] });
    } else {
        const [f0, f1, ...reste] = facteurs;
        c.push({ code: 'produit_de_tables', facteurs: [{ p: f0.p * f1.p, exp: 1 }, ...reste] });
    }
    if (facteurs.some((f) => f.exp >= 2)) c.push({ code: 'exposant_oublie', facteurs: facteurs.map((f) => ({ p: f.p, exp: 1 })) });
    const iMax = facteurs.reduce((im, f, i) => (f.exp > facteurs[im].exp ? i : im), 0);
    c.push({ code: 'exposant_faux', facteurs: facteurs.map((f, i) => (i === iMax ? { ...f, exp: f.exp + 1 } : f)) });
    c.push({ code: 'facteur_manquant', facteurs: facteurs.slice(0, -1) });
    return c;
};

const genQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const it = tirer({ max: randInt(1, 3) === 1 ? 200 : 100 });
        verifier(it.n, it.facteurs);
        const bon = tex(decompoTex(it.facteurs));
        const faux = [];
        // produit_de_tables toujours (la principale), puis deux familles tirées
        const cands = candidats(it);
        for (const cand of [cands.find((c) => c.code === 'produit_de_tables'), ...shuffle(cands.filter((c) => c.code !== 'produit_de_tables'))]) {
            const texte = tex(decompoTex(cand.facteurs));
            if (texte === bon || faux.some((f) => f.texte === texte)) continue;
            // même valeur admise pour produit_de_tables seulement (faux par la forme)
            if (cand.code !== 'produit_de_tables' && valeurDe(cand.facteurs) === it.n) continue;
            faux.push({ code: cand.code, texte });
        }
        if (faux.length < 3) continue;
        const choix = [bon, ...faux.slice(0, 3).map((f) => f.texte)];
        const payload = makeQcm(choix, bon, `<p><b>Quelle est la décomposition de ${it.n} en produit de facteurs premiers ?</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.slice(0, 3).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 90 = 2 × 3² × 5 ; 2 × 9 × 5 (produit_de_tables), 2 × 3 × 5 (exposant_oublie), 2 × 3³ × 5 (exposant_faux).
    const facteurs = [{ p: 2, exp: 1 }, { p: 3, exp: 2 }, { p: 5, exp: 1 }];
    const bon = tex(decompoTex(facteurs));
    const payload = makeQcm([bon, tex('2 \\times 9 \\times 5'), tex('2 \\times 3 \\times 5'), tex('2 \\times 3^{3} \\times 5')], bon, '<p><b>Quelle est la décomposition de 90 en produit de facteurs premiers ?</b></p>', opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { n: 90, facteurs, distracteurs: [{ code: 'produit_de_tables' }, { code: 'exposant_oublie' }, { code: 'exposant_faux' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

/** Une ligne pour un nombre : égalité en valeur (vraie, ou fausse par une famille), ou « la décomposition est … » (forme). */
const ligneVf = (it, vraie) => {
    const { n, facteurs } = it;
    verifier(n, facteurs);
    const type = randomChoice(['egalite', 'egalite', 'forme']);
    if (type === 'forme') {
        if (vraie) return { ligne: `La décomposition de ${n} en produit de facteurs premiers est ${tex(decompoTex(facteurs))}.`, code: null };
        const cand = candidats(it).find((c) => c.code === 'produit_de_tables');
        return { ligne: `La décomposition de ${n} en produit de facteurs premiers est ${tex(decompoTex(cand.facteurs))}.`, code: 'produit_de_tables' };
    }
    if (vraie) return randomChoice([true, false])
        ? { ligne: tex(`${n} = ${decompoTex(facteurs)}`), code: null }
        : { ligne: tex(`${decompoTex(facteurs)} = ${developpeTex(facteurs)}`), code: null };
    const cands = candidats(it).filter((c) => valeurDe(c.facteurs) !== n);
    const iCube = facteurs.findIndex((f) => f.exp === 3);
    if (iCube >= 0 && randomChoice([true, false])) {
        // base_fois_exposant : 2³ lu 2 × 3
        const faux = facteurs.reduce((acc, f, i) => acc * (i === iCube ? f.p * f.exp : f.p ** f.exp), 1);
        if (faux !== n) return { ligne: tex(`${decompoTex(facteurs)} = ${faux}`), code: 'base_fois_exposant' };
    }
    const d = randomChoice(cands);
    return { ligne: tex(`${n} = ${decompoTex(d.facteurs)}`), code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 100; essai++) {
        const items = [];
        for (let i = 0; i < 4; i++) items.push(tirer({ max: i === 0 ? 200 : 100, avecExposant: i < 2 ? true : null, exclure: items.map((it) => it.n) }));
        if (new Set(items.map((it) => it.n)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => ligneVf(it, verites[i]));
        if (new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 90 = 2 × 3² × 5 V ; 30 = 2 × 3 × 5 V ; 2³ × 7 = 42 F (base_fois_exposant) ; 28 = 2 × 7 F (exposant_oublie).
    const lignes = [tex('90 = 2 \\times 3^{2} \\times 5'), tex('30 = 2 \\times 3 \\times 5'), tex('2^{3} \\times 7 = 42'), tex('28 = 2 \\times 7')];
    const payload = makeVraiFaux(lignes, [0, 0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [], verites: [true, true, false, false], distracteurs: [{ code: 'base_fois_exposant' }, { code: 'exposant_oublie' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix : la clé et trois familles ; produit_de_tables a la MÊME VALEUR que la clé (faux par la forme : un facteur non premier), la seule exception assumée ; les autres ont une valeur différente',
        'facteurs 2, 3, 5, 7, écrits croissants, exposants en KaTeX ; nombres 12..100 (deux fois sur trois) ou ≤ 200',
        'sacrifié : 60 (l\'exemple du programme) sur tout ce qui s\'affiche',
    ],
    qcm_unique: {
        familles: {
            produit_de_tables: '4 × 3 × 5 pour 60 (2² lu 4) ou 6 × 5 pour 30 (2 × 3 fusionnés), la principale : la réponse de 1041, vraie et pas finie',
            exposant_oublie: 'tous les exposants à 1 (2 × 3 × 5 = 30)',
            exposant_faux: '+1 sur le plus grand exposant (2³ × 3 × 5 = 120)',
            facteur_manquant: 'le plus grand facteur retiré (2² × 3 = 12)',
        },
    },
    vf_tableau_vf: 'quatre nombres distincts, un ≤ 200, deux avec exposant ; lignes en valeur (n = décomposition ; décomposition = écriture développée ; base_fois_exposant sur un cube) ou en forme (« la décomposition de n est 4 × 15 » F)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    decomposition_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'decomposition', label: L_QCM, formatsList: [{ key: 'decomposition_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

